import React, { useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"

// styles
import Styles from "./spotWalletTab.module.css"

// icons
import { BiWallet } from "react-icons/bi"
import { RiShareBoxFill } from "react-icons/ri"
import { BsCreditCard2Back } from "react-icons/bs"
import { FiEye, FiEyeOff, FiDownload, FiUpload, FiClock } from "react-icons/fi"

// components
import Search from "../../../../components/Search/Search"
import Button from "../../../../components/Button/Button"
import CoinTag from "../../../../components/CoinTag/CoinTag"
import TableButton from "../../../../components/TableButton/TableButton"
import MUIDatagrid from "../../../../components/MUIDatagrid/MUIDatagrid"
import Pagination from "@mui/material/Pagination"

// redux
import { useSelector, useDispatch } from "react-redux"
import { _getDepositAddress } from "../../../../redux/actions"
import { setBackdropOpen } from "../../../../redux/slices/backdropSlice"
import { _getCurrencies, _getBalances } from "../../../exchange/redux/actions"
import {
  setSnackbarOpen,
  setSnackbarData
} from "../../../snackbar/redux/snackbarSlice"
import {
  setSelectedCoinId,
  setDepositAddress
} from "../../../../redux/slices/depositSlice"
import {
  setWarningModalData,
  setWarningModalOpen
} from "../../../modals/warning/redux/warningModalSlice"
import {
  setWalletDepositModalData,
  setWalletDepositModalOpen
} from "../../../modals/deposit-withdraw/redux/walletDepositModalSlice"
import {
  setSelectCurrencyModalData,
  setSelectCurrencyModalOpen
} from "../../../modals/select-currency/redux/selectCurrencyModalSlice"

// utils
import { FormattedMessage, injectIntl } from "react-intl"
import { formatNumber } from "../../../helpers/formatNumber"
import {
  setWithdrawFormModalOpen,
  setwithdrawFormModalData
} from "../../../modals/deposit-withdraw/redux/withdrawFormModalSlice"
import { showErrorSnackbar } from "../../../snackbar/helpers"

function SpotWalletTab() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()

  const theme = useSelector((state) => state.theme.value)

  const TotalWithdraws = useSelector(
    (state) => state.fundsData.data.total_withdrawals
  )
  const TotalDeposits = useSelector(
    (state) => state.fundsData.data.total_deposits
  )
  const [selectedTab, setSelectedTab] = useState("all")
  const [testCurrencies, setTestCurrencies] = useState([])
  const [balances, setBalances] = useState([])
  const [filteredBalances, setFilteredBalances] = useState([])
  const [showPrice, setShowPrice] = useState(true)
  const [tableLoading, setTableLoading] = useState(false)

  // Pagination settings
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalPages, setTotalPages] = useState(1)

  const handleWalletNotFound = () => {
    dispatch(setWarningModalOpen(true))
    dispatch(
      setWarningModalData({
        title: "Wallet Address Not Found",
        subtitle: "There was no wallet found for the selected coin",
        callback: () => dispatch(setWarningModalOpen(false))
      })
    )
  }

  const handleSearch = (e) => {
    const inputValue = e.target.value

    if (inputValue === "" || inputValue === null || inputValue === undefined) {
      setFilteredBalances(balances)
      return
    }
    setFilteredBalances(
      balances.filter((el) => {
        return (
          el.id.toLowerCase().includes(inputValue.toLowerCase()) ||
          el.name.toLowerCase().includes(inputValue.toLowerCase())
        )
      })
    )
  }

  useEffect(() => {
    setTableLoading(true)
    setFilteredBalances([])
    _getCurrencies((currencydata) => {
      setTestCurrencies(currencydata)
      _getBalances(
        (data) => {
          setBalances(() => {
            const newBalances = currencydata.map((currency, index) => {
              return {
                ...currency,
                balance:
                  data.find((wallet) => wallet.currency === currency.id)
                    ?.balance ?? 0.0,
                locked:
                  data.find((wallet) => wallet.currency === currency.id)
                    ?.locked ?? 0.0
              }
            })
            let filtered = newBalances
            if (selectedTab === "available") {
              filtered = newBalances.filter(
                (balance) => Number(balance.balance) > 0
              )
            }

            // Calculate total pages
            setTotalPages(Math.ceil(filtered.length / rowsPerPage))

            // Apply pagination to filtered data
            const startIndex = (page - 1) * rowsPerPage
            const paginatedBalances = filtered.slice(startIndex, startIndex + rowsPerPage)

            setFilteredBalances(paginatedBalances)
            return newBalances
          })
          setTableLoading(false)
        },
        (err) => setTableLoading(false)
      )
    })
  }, [selectedTab, page, rowsPerPage])

  useEffect(() => {
    if (location.pathname === "/account/profile/wallet/all") {
      setSelectedTab("all")
    }
    if (location.pathname === "/account/profile/wallet/available") {
      setSelectedTab("available")
    }
  }, [location])

  const handlePageChange = (event, newPage) => {
    setPage(newPage)
  }

  const handleDeposit = (coin_id) => {
    dispatch(
      setWalletDepositModalData({
        title: "",
        qr: null,
        destination: null,
        callback: () => { }
      })
    )
    dispatch(setBackdropOpen(true))
    _getDepositAddress(
      {
        coin: coin_id
      },
      (data) => {
        // dispatch(setBackdropOpen(false))
        if (data.address === null) {
          setTimeout(() => {
            _getDepositAddress(
              {
                coin: coin_id
              },
              (data) => {
                if (data.address !== null) {
                  dispatch(setBackdropOpen(false))
                  setSelectCurrencyModalOpen(false)
                  dispatch(setDepositAddress(data))
                  dispatch(setWalletDepositModalOpen(true))
                } else {
                  dispatch(setBackdropOpen(false))
                  handleWalletNotFound()
                }
              },
              (error) => {
                dispatch(setBackdropOpen(false))
                dispatch(setSnackbarOpen(true))
                dispatch(
                  setSnackbarData({
                    alertMessage: error.errors[0].length ? (
                      <FormattedMessage id={error.errors[0]} />
                    ) : (
                      "Unknown Error"
                    ),
                    severity: "error",
                    callback: () => { },
                    position: "topRight"
                  })
                )
              }
            )
          }, 15000) // Delay of 15000 milliseconds (15 seconds)
        } else {
          dispatch(setBackdropOpen(false))
          dispatch(setDepositAddress(data))
          dispatch(setWalletDepositModalOpen(true))
        }
      },
      (error) => {
        dispatch(setBackdropOpen(false))
        dispatch(setSnackbarOpen(true))
        dispatch(
          setSnackbarData({
            alertMessage: error.errors[0].length ? (
              <FormattedMessage id={error.errors[0]} />
            ) : (
              "Unknown Error"
            ),
            severity: "error",
            callback: () => { },
            position: "topRight"
          })
        )
      }
    )
  }

  // ----- RESPONSIVENESS STATE ---------
  // ------------------------------------
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 820)

  const handleResize = () => {
    setIsMobileView(window.innerWidth < 820)
  }

  useEffect(() => {
    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])
  // ------------------------------------
  // ------------------------------------

  const walletColumns = isMobileView
    ? [
      {
        minWidth: 180,
        flex: 1,
        field: "currency",
        headerName: "Coin",
        renderCell: (data) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              marginRight: 10
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 5
              }}
            >
              <img width={34} src={data.row?.icon_url} alt={data.row?.name} />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 5,
                  marginBottom: -10
                }}
              >
                <span
                  style={{
                    fontSize: 15
                  }}
                >
                  {data.row?.name}
                </span>
                <CoinTag tag={data.row?.id.toUpperCase()} size={10} />
              </div>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                textAlign: "right"
              }}
            >
              <span
                style={{
                  fontSize: 14
                }}
              >
                {showPrice ? formatNumber(data.row.balance) : "*****"}
              </span>
              <span
                style={{
                  fontSize: 12,
                  color: "var(--gray-color)"
                }}
              >
                {showPrice
                  ? formatNumber(
                    parseFloat(data.row.balance) +
                    parseFloat(data.row.locked)
                  ) ?? "0.00"
                  : "******"}
              </span>
            </div>
          </div>
        )
      }
    ]
    : [
      {
        minWidth: 180,
        flex: 1,
        field: "currency",
        headerName: "Coin",
        renderCell: (data) => (
          <div className={Styles.coinNameColumn}>
            <img src={data.row?.icon_url} alt={data.row?.name} />
            <span>{data.row?.name}</span>
            <CoinTag tag={data.row?.id.toUpperCase()} size={12} />
          </div>
        )
      },
      {
        minWidth: 100,
        flex: 1,
        field: "balance",
        headerName: "Avbl. Balance",
        renderCell: (data) => (
          <div>{showPrice ? data.row.balance : "******"}</div>
        )
      },
      {
        minWidth: 65,
        maxWidth: 100,
        flex: 1,
        field: "locked",
        headerName: "Locked",
        renderCell: (data) => (
          <div>{showPrice ? formatNumber(data.row.locked) : "******"}</div>
        )
      },
      {
        minWidth: 120,
        maxWidth: 135,
        flex: 1,
        field: "amount",
        headerName: "Amount",
        renderCell: (data) => (
          <div>
            {showPrice
              ? formatNumber(
                parseFloat(data.row.balance) + parseFloat(data.row.locked)
              ) ?? "0.00"
              : "******"}
          </div>
        )
      },
      {
        minWidth: 260,
        flex: 1,
        headerName: "Action",
        renderCell: (data) => (
          <div className={Styles.buttonsContainer}>
            <TableButton
              buttonText="Deposit"
              buttonIcon={<RiShareBoxFill />}
              onClick={() => {
                handleDeposit(data.row.id)
              }}
            />
            <TableButton
              buttonText="Withdraw"
              buttonIcon={<RiShareBoxFill />}
              onClick={() => {
                dispatch(
                  setwithdrawFormModalData({
                    selectedCoinID: data.row.id,
                    callback: () => {
                      // console.log("HERE") // call API here to proceed
                    }
                  })
                )
                dispatch(setWithdrawFormModalOpen(true))
              }}
            />
            <TableButton
              buttonText="Convert"
              buttonIcon={<RiShareBoxFill />}
              onClick={() =>
                navigate(`/convert/${data.row.id.toUpperCase()}_USD`)
              }
            />
          </div>
        )
      }
    ]

  return (
    <>
      {/* tab header */}
      <div className={`${Styles.card} ${Styles.tabHeader}`}>
        {/* left section */}
        <div>
          <div className={Styles.TitlesContainer}>
            <h3>Spot Wallet</h3>
            <span className={Styles.subTitle}>
              All your crypto techs, stored in one place. Cool huh?
            </span>
          </div>

          <div>
            <BiWallet size={20} />
            Wallet Balance
            <span className={Styles.walletBalanceSpanMobile}>
              {showPrice ? `${formatNumber(0)}` : "********"}
            </span>
            <button
              onClick={() => setShowPrice(!showPrice)}
              className={Styles.hidePriceButtonMobile}
            >
              {showPrice ? (
                <FiEyeOff color="var(--gray-color)" />
              ) : (
                <FiEye color="var(--gray-color)" />
              )}
            </button>
          </div>

          <div>
            <span>{showPrice ? `${formatNumber(0)}` : "********"}</span>
            <button
              className={Styles.hidePriceButton}
              onClick={() => setShowPrice(!showPrice)}
            >
              {`${showPrice ? "Hide" : "Show"} Price`}{" "}
              {showPrice ? <FiEyeOff /> : <FiEye />}
            </button>
          </div>
        </div>

        {/* right section */}
        <div>
          <div>
            <Button
              text="Deposit"
              style={{
                flex: 0.45,
                minHeight: 33,
                height: 33,
                borderRadius: 5
              }}
              onClick={() => {
                dispatch(
                  setSelectCurrencyModalData({
                    title: "Select coin to deposit",
                    showTabs: false,
                    type: "coin",
                    searchPlaceholder: "Search coin name",
                    currenciesToHide: [],
                    disableBackdropClick: true,
                    callback: (coin) => {
                      dispatch(setSelectedCoinId(coin.id))
                      dispatch(setBackdropOpen(true))
                      _getDepositAddress(
                        {
                          coin: coin.id
                        },
                        (data) => {
                          // console.log(data)
                          if (data.address === null) {
                            setTimeout(() => {
                              _getDepositAddress(
                                {
                                  coin: coin.id
                                },
                                (data) => {
                                  dispatch(setBackdropOpen(false))
                                  if (data.address) {
                                    setSelectCurrencyModalOpen(false)
                                    dispatch(setDepositAddress(data))
                                    dispatch(setWalletDepositModalOpen(true))
                                  } else {
                                    handleWalletNotFound()
                                  }
                                },
                                (err) => {
                                  dispatch(setBackdropOpen(false))
                                  handleWalletNotFound()
                                }
                              )
                            }, 15000) // Delay of 15000 milliseconds (15 seconds)
                          } else {
                            dispatch(setBackdropOpen(false))
                            setSelectCurrencyModalOpen(false)
                            dispatch(setDepositAddress(data))
                            dispatch(setWalletDepositModalOpen(true))
                          }
                        },
                        (err) => {
                          if (err && err.errors) {
                            showErrorSnackbar({ alertMessage: err.errors[0].length ? (<FormattedMessage id={err.errors} />) : "Failed to get deposit address" })
                          }
                          dispatch(setBackdropOpen(false))
                        }
                      )
                    }
                  })
                )
                dispatch(setSelectCurrencyModalOpen(true))
              }}
            />
            <Button
              text="Withdraw"
              variant="dark"
              style={{
                flex: 0.45,
                minHeight: 33,
                height: 33,
                borderRadius: 5,
                backgroundColor: theme === "light" && "#D5F9F9",
                color: theme === "light" && "#001e28"
              }}
              onClick={() => {
                dispatch(
                  setSelectCurrencyModalData({
                    title: "Select coin",
                    showTabs: false,
                    type: "coin",
                    showMarketPairs: false,
                    currenciesToHide: [],
                    disableBackdropClick: false,
                    callback: (coin) => {
                      dispatch(
                        setwithdrawFormModalData({
                          selectedCoinID: coin.id,
                          callback: () => {
                            // console.log("HERE") // call API here to proceed
                          }
                        })
                      )
                      dispatch(setWithdrawFormModalOpen(true))
                    }
                  })
                )
                dispatch(setSelectCurrencyModalOpen(true))
              }}
            />
          </div>

          <div
            style={{
              backgroundColor: "var(--background-color)"
            }}
          >
            <div>
              <span>
                <BsCreditCard2Back />
                Total Deposited
              </span>
              <span>
                <FiDownload color="var(--primary-color)" />$
                {formatNumber(TotalDeposits)} USD
              </span>
            </div>

            <div>
              <span>
                <FiClock />
                Total Withdrawals
              </span>
              <span>
                <FiUpload color="var(--primary-color)" />$
                {formatNumber(TotalWithdraws)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* wallets */}
      <div className={`${Styles.card} ${Styles.walletsContainer}`}>
        {/* table header */}
        <div className={Styles.tableHeader}>
          <h4>Spot Wallet</h4>
          <Search
            placeholder="Search wallets"
            onChange={handleSearch}
            containerStyle={{ width: isMobileView && "100%" }}
          />
        </div>
        <div className={Styles.tabsContainer}>
          <Button
            text="All"
            type="tab"
            onClick={() => {
              if (tableLoading) return
              navigate("/account/profile/wallet/all", { replace: true })
            }}
            isTabSelected={location.pathname === "/account/profile/wallet/all" || location.pathname === "/account/profile/wallet"}
          />
          <Button
            text="Available"
            type="tab"
            onClick={() => {
              if (tableLoading) return
              navigate("/account/profile/wallet/available", { replace: true })
            }}
            isTabSelected={
              location.pathname === "/account/profile/wallet/available"
            }
            style={{ marginRight: "auto" }}
          />
        </div>

        {/* MUI DATAGRID HERE */}
        <MUIDatagrid
          rows={filteredBalances}
          loading={tableLoading}
          getRowId={(row) => row.id}
          columns={walletColumns}
          rowHeight={isMobileView && 80} //original 210
          hideFooter={false}
          hideHeader={isMobileView}
          pageSize={rowsPerPage}
          rowCount={balances.length}
          paginationMode="server"
          onPageChange={handlePageChange}
          page={page}
        />

        {/* Custom pagination component if MUIDatagrid pagination doesn't work as expected */}
        <div className={Styles.paginationWrapper}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
            size={isMobileView ? "small" : "medium"}
          />
        </div>
      </div>
    </>
  )
}

export default injectIntl(SpotWalletTab)
